import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { useState } from "@faire/web--source/ui/hooks/useState";
import { QueryParameters as SelfServeQueryParameters } from "@faire/web-api--source/routes/support/self-serve";
import { useEffect } from "react";

import { useNavigationStore } from "./NavigationContext";
import { useNodeSetter } from "./useNodeSetter";
import { useTree } from "./useTree";

type SkipNodeSelection = {
  skipOrderSelection: boolean;
  nodeSelected: boolean;
  setNodeSelected: (nodeSelected: boolean) => void;
};

export const useSkipNodeSelection = (): SkipNodeSelection => {
  const { tree } = useTree();
  const [skipOrderSelection, setSkipOrderSelection] = useState<boolean>(false);
  const [, setNavigationStore] = useNavigationStore(["hasLevelQueryParams"]);
  const { l1, l2, l3, l4, l5 } = useQueryParams(SelfServeQueryParameters.parse);
  const {
    setLevel1Node,
    setLevel2Node,
    setLevel3Node,
    setLevel4Node,
    setLevel5Node,
  } = useNodeSetter();
  // This is used to fix the race condition where the user clicks on a topic to go to next page before L1 topic is set
  const [nodeSelected, setNodeSelected] = useState<boolean>(false);

  useEffect(() => {
    if (l1) {
      const level1Node = tree[l1];
      if (level1Node) {
        setLevel1Node(level1Node);
        setNodeSelected(true);
        setSkipOrderSelection(true);
        setNavigationStore({ hasLevelQueryParams: true });
      }
    }
  }, [l1, setLevel1Node, setNavigationStore, tree]);

  useEffect(() => {
    if (l2) {
      setNodeSelected(true);
      const level2Node = tree[l2];
      if (level2Node) {
        setLevel2Node(level2Node);
      }
    }
    if (l3) {
      setNodeSelected(true);
      const level3Node = tree[l3];
      if (level3Node) {
        setLevel3Node(level3Node);
      }
    }
    if (l4) {
      setNodeSelected(true);
      const level4Node = tree[l4];
      if (level4Node) {
        setLevel4Node(level4Node);
      }
    }
    if (l5) {
      setNodeSelected(true);
      const level5Node = tree[l5];
      if (level5Node) {
        setLevel5Node(level5Node);
      }
    }
  }, [
    l2,
    l3,
    l4,
    l5,
    setLevel2Node,
    setLevel3Node,
    setLevel4Node,
    setLevel5Node,
    tree,
  ]);

  return {
    skipOrderSelection,
    nodeSelected,
    setNodeSelected,
  };
};
